import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Loader2, FlaskConical, CheckCircle2, TriangleAlert } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

// ===============================
// Types
// ===============================

type PatientRecord = {
  id: string;
  content?: string;
  text?: string;
  created_at?: string;
  results_generated?: boolean;
  document_id?: string | null;
  observed_at?: string;
};

type Subcategory = {
  id: string;
  category_id?: string;
  name: string;
  show_chart?: boolean;
};

type CandidateHit = {
  record_id: string;
  document_id?: string | null;
  subcategory_id: string;
  subcategory_name: string;
  value: number;
  unit?: string;
  snippet: string;
  observed_at?: string;
};

type GroupedSummary = {
  subcategory_id: string;
  subcategory_name: string;
  total: number;
  unit_examples: string[];
  items: CandidateHit[];
};

// ===============================
// Utilities
// ===============================

function normalizeLettersOnly(input: string) {
  return (input || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function fuzzyContains(recordText: string, subcategoryName: string) {
  const a = normalizeLettersOnly(recordText);
  const b = normalizeLettersOnly(subcategoryName);
  if (!a || !b) return false;

  // Busca completa (comportamento original)
  if (a.includes(b)) return true;

  // Busca por primeira palavra-chave da subcategoria
  // Ex: "Pressão arterial" → busca por "pressao"
  const firstWord = b.split(/\s+/)[0];
  if (firstWord.length >= 4 && a.includes(firstWord)) {
    return true;
  }

  return false;
}

const numberUnitRegex = /(?<value>[\-+]?\d{1,3}(?:[\.,]\d{1,3})?)\s*(?<unit>(?:mg\/?d?l|g\/?d?l|mmol\/l|mEq\/l|u\/l|U\/L|UI\/L|%|mmhg|bpm|ng\/ml|µg\/dl|ug\/dl|mg\/l|g\/l|mU\/l|mIU\/l|pg\/ml|mL|min|h|day|dias|kg|g|mg|mcg|µg|ng|mm|cm|m|L)\b)?/i;

function parseLocalizedNumber(n: string): number | null {
  if (!n) return null;
  const normalized = n.replace(/\./g, "").replace(/,/g, ".");
  const val = Number(normalized);
  return isNaN(val) ? null : val;
}

function extractValueNearTerm(text: string, term: string): CandidateHit[] {
  const hits: CandidateHit[] = [];
  if (!text || !term) return hits;

  if (!fuzzyContains(text, term)) return hits;

  const raw = text;
  const rawLower = raw.toLowerCase();
  const termLower = term.toLowerCase();
  const normTerm = normalizeLettersOnly(term);
  let start = 0;
  const windowSize = 140;

  // Detectar se é contexto de pressão arterial
  const isBloodPressure = /pressao|arterial|pa\b/i.test(normTerm);

  // Usar busca por palavra-chave (primeira palavra) no indexOf
  const searchTerm = (() => {
    const words = termLower.split(/\s+/);
    if (words.length > 1 && words[0].length >= 4) {
      return words[0];
    }
    return termLower;
  })();

  while (true) {
    const idx = rawLower.indexOf(searchTerm, start);
    if (idx === -1) break;
    const slice = raw.slice(idx, Math.min(raw.length, idx + term.length + windowSize));

    // Priorizar regex de pressão arterial se aplicável
    if (isBloodPressure) {
      const bpRegex = /(?<systolic>\d{2,3})\s*[\/x]\s*(?<diastolic>\d{2,3})/gi;
      let bpMatch;
      while ((bpMatch = bpRegex.exec(slice)) !== null) {
        const systolic = parseInt(bpMatch.groups!.systolic);
        const diastolic = parseInt(bpMatch.groups!.diastolic);

        // Validação: aceitar formato reduzido (12/8) e completo (120/80)
        if (
          (systolic >= 10 && systolic <= 25 && diastolic >= 5 && diastolic <= 20) ||
          (systolic >= 60 && systolic <= 250 && diastolic >= 40 && diastolic <= 150)
        ) {
          // Calcular valor combinado no formato sistólica.diastólica (ex: 12/8 → 12.08)
          const combinedValue = parseFloat(`${systolic}.${diastolic.toString().padStart(2, '0')}`);

          const snippet = slice
            .replace(/\s+/g, " ")
            .slice(0, 120)
            .trim();
          hits.push({
            record_id: "",
            subcategory_id: "",
            subcategory_name: term,
            value: combinedValue,
            unit: "mmHg",
            snippet,
          });
        }
      }
    }

    // Regex existente para valores simples
    const m = numberUnitRegex.exec(slice);
    if (m && m.groups?.value) {
      const value = parseLocalizedNumber(m.groups.value);
      const unit = m.groups.unit?.trim();
      if (value !== null) {
        const snippet = slice
          .replace(/\s+/g, " ")
          .slice(0, 120)
          .trim();
        hits.push({
          record_id: "",
          subcategory_id: "",
          subcategory_name: term,
          value,
          unit,
          snippet,
        });
      }
    }
    start = idx + searchTerm.length;
  }

  return hits;
}

function groupBySubcategory(items: CandidateHit[]): GroupedSummary[] {
  const map = new Map<string, GroupedSummary>();
  for (const it of items) {
    if (!map.has(it.subcategory_id)) {
      map.set(it.subcategory_id, {
        subcategory_id: it.subcategory_id,
        subcategory_name: it.subcategory_name,
        total: 0,
        unit_examples: [],
        items: [],
      });
    }
    const g = map.get(it.subcategory_id)!;
    g.total += 1;
    if (it.unit && !g.unit_examples.includes(it.unit)) g.unit_examples.push(it.unit);
    g.items.push(it);
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

function formatDisplayValue(value: number, unit?: string): string {
  // Detectar pressão arterial: unit = "mmHg" e valor < 100
  if (unit === "mmHg" && value < 100) {
    const systolic = Math.floor(value);
    const diastolic = Math.round((value % 1) * 100);
    return `${systolic}/${diastolic}`;
  }

  // Para outros valores, retornar como string simples
  return value.toString();
}

// ===============================
// Main Component
// ===============================

interface AnalyzeRecordsButtonProps {
  patientId: string;
  className?: string;
  onSuccess?: () => void;
}

export function AnalyzeRecordsButton({ patientId, className, onSuccess }: AnalyzeRecordsButtonProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState<CandidateHit[]>([]);
  const [saving, setSaving] = useState(false);

  const grouped = useMemo(() => groupBySubcategory(candidates), [candidates]);

  async function onAnalyze() {
    try {
      setLoading(true);

      // ✅ REMOVIDO: patient_health_records não é mais usado
      const records: any[] = [];

      // 2) Load subcategories
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const { data: subs, error: subsError } = await supabase
        .from("lab_subcategories")
        .select("id, category_id, name, show_chart")
        .eq("user_id", user.id);

      if (subsError) throw new Error(`Erro ao carregar subcategorias: ${subsError.message}`);

      // 3) Filter records without results
      const target = (records as PatientRecord[] || []).filter((r) => !r.results_generated);
      if (target.length === 0) {
        toast({
          title: "Nada a analisar",
          description: "Todos os registros já foram processados."
        });
        return;
      }

      // 4) Scan each record against each subcategory
      const found: CandidateHit[] = [];
      for (const r of target) {
        const recordText = r.content || r.text || "";
        for (const s of (subs as Subcategory[]) || []) {
          const hits = extractValueNearTerm(recordText, s.name);
          for (const h of hits) {
            found.push({
              ...h,
              record_id: r.id,
              document_id: r.document_id,
              subcategory_id: s.id,
              subcategory_name: s.name,
              observed_at: r.observed_at,
            });
          }
        }
      }

      if (found.length === 0) {
        toast({
          title: "Nenhum resultado identificado",
          description: "Não encontramos valores correspondentes às subcategorias nos registros pendentes."
        });
        setCandidates([]);
        setOpen(false);
        return;
      }

      setCandidates(found);
      setOpen(true);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Erro ao analisar",
        description: e instanceof Error ? e.message : "Tente novamente."
      });
    } finally {
      setLoading(false);
    }
  }

  async function onGenerate() {
    try {
      setSaving(true);

      // Create lab results in bulk
      const resultsToCreate = candidates.map(c => ({
        patient_id: patientId,
        source_document_id: c.document_id || null,
        test_name: c.subcategory_name,
        value: c.value,
        unit: c.unit || null,
        subcategory_id: c.subcategory_id,
        collected_at: c.observed_at?.split('T')[0] || new Date().toISOString().split('T')[0]
      }));

      const { error: insertError } = await supabase
        .from("lab_results_onboarding")
        .insert(resultsToCreate);

      if (insertError) throw new Error(`Erro ao criar resultados: ${insertError.message}`);

      // ✅ REMOVIDO: patient_health_records não é mais usado

      toast({
        title: "Resultados gerados",
        description: `${resultsToCreate.length} registros criados com sucesso.`
      });
      setOpen(false);
      setCandidates([]);

      // Atualizar a listagem de resultados no componente pai
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Falha ao gerar resultados",
        description: e instanceof Error ? e.message : "Tente novamente."
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Button
        className={className}
        onClick={onAnalyze}
        disabled={loading}
        variant="default"
        size="sm"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Analisando…
          </>
        ) : (
          <>
            <FlaskConical className="h-4 w-4 mr-2" />
            Analisar registros
          </>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Pré-visualização de resultados encontrados</DialogTitle>
            <DialogDescription>
              Identificamos valores numéricos próximos às subcategorias nos registros. Revise o resumo e confirme a geração.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {candidates.length} ocorrência(s) em {grouped.length} subcategoria(s)
              </div>
            </div>

            <Separator />

            <ScrollArea className="h-[360px]">
              {grouped.map((g) => (
                <div key={g.subcategory_id} className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="secondary" className="text-sm">
                      {g.subcategory_name}
                    </Badge>
                    <span className="text-sm text-muted-foreground">Total: {g.total}</span>
                    {g.unit_examples.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        Unidades: {g.unit_examples.join(", ")}
                      </span>
                    )}
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Valor</TableHead>
                        <TableHead>Unidade</TableHead>
                        <TableHead>Contexto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {g.items.slice(0, 5).map((it, idx) => (
                        <TableRow key={`${it.record_id}-${idx}`}>
                          <TableCell className="font-medium">{formatDisplayValue(it.value, it.unit)}</TableCell>
                          <TableCell>{it.unit ?? "—"}</TableCell>
                          <TableCell className="text-xs">
                            {it.snippet}
                            {it.document_id && (
                              <Badge variant="outline" className="ml-2 text-[10px]">📄 Doc</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableCaption className="text-left">
                      {g.total} resultado(s) para {g.subcategory_name}
                      {g.items.length > 5 && ` (mostrando 5 de ${g.items.length})`}
                    </TableCaption>
                  </Table>
                </div>
              ))}
            </ScrollArea>
          </div>

          <DialogFooter className="flex items-center justify-between gap-2 mt-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <TriangleAlert className="h-4 w-4" />
              Confirme apenas se os valores parecem corretos.
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={onGenerate} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Gerando…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Gerar resultados
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
